
import Vue from 'vue';
import Component from 'vue-class-component';
import PhotosModule, { Conflict, photoQueueStatus } from '@/store/modules/Photos';
import store from '@/store/index';
import { Prop, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import ElementProgress from '@/components/ElementProgress.vue';
import ConfirmDelete from './ConfirmDelete.vue';
import ConfirmationModel from '../ConfirmationModel';
import Intersect from 'vue-intersect';
import Dropzone, { FileStructure } from '@/decorators/Dropzone';
import Session from '@/model/Session';
import UploadModule from '@/store/modules/Upload';
import StandardHover from './photoHovers/StandardHover.vue';
import ConflictHover from './photoHovers/ConflictHover.vue';
import { replaceBus } from '../ReplaceDialog.vue';
import ConfirmFinalize from './ConfirmFinalize.vue';
import SwitchedContainer from '@/components/price-list/form/inputs/containers/SwitchedContainer.vue';
import api from '@/service/api';
import { PriceList } from '@/model/PriceList';
import { groupOperationBus } from '@/components/GroupOperations.vue';

const photosModule = getModule(PhotosModule, store);
const uploadModule = getModule(UploadModule, store);

@Component({
    components: {
        ElementProgress,
        ConfirmDelete,
        ConfirmFinalize,
        Intersect,
        StandardHover,
        ConflictHover,
        SwitchedContainer,
    },
})
@Dropzone
export default class Photo extends Vue {
    @Prop({ type: Number, default: 0 })
    public index!: number;

    @Prop({ type: Boolean, default: true })
    public revealed!: boolean;
    @Prop({
        type: Boolean,
        required: false,
        default: false,
    })
    public ordered!: boolean;
    @Prop({
        type: Boolean,
        required: false,
        default: false,
    })
    public finalized!: boolean;
    @Prop({ type: Number, default: null })
    public surcharge!: number|null;
    @Prop({ type: String, default: 'all' })
    public saleMode!: 'all'|'noDigital'|'digitalOnly';
    @Prop({
        type: String,
        required: false,
    })
    public photoId?: string;
    @Prop({
        type: File,
        required: false,
    })
    public photoFile?: File;
    @Prop({
        type: Boolean,
        required: false,
        default: false,
    })
    public checked!: boolean;
    @Prop({
        type: String,
        required: true,
    })
    public filename!: string;
    @Prop({
        type: Session,
        required: true,
    })
    public session!: Session;
    public overlayVisible = false;
    public delConfirmationModel: ConfirmationModel = new ConfirmationModel();
    public finConfirmationModel: ConfirmationModel = new ConfirmationModel();
    public loading: boolean = false;
    public intersected: boolean = false;
    public recommendationInProgress = false;
    public saleDialogOpen = false;
    public saleDialogPhotoIds: string[] = [];
    public saleDialogFromGroup = false;
    public saleDialogMixed = false;
    public groupSaleOptionsEdited = false;
    public surchargeEnabled = false;
    public reductionEnabled = false;
    public draftSurcharge = '0';
    public draftReduction = '0';
    public draftSaleMode: 'all'|'noDigital'|'digitalOnly' = 'all';
    public savingSaleOptions = false;
    private saleDialogRequestId = 0;
    private onOpenGroupSaleOptions = (photoIds: string[]) => {
        if (this.photoId === photoIds[0]) this.openSaleDialog(photoIds);
    };
    public digitalSaleEnabled: boolean|null = null;
    public priceList: PriceList|null = null;
    public filenameOverflow = 0;
    public filenameCycle = 10;
    public conflict = null as Conflict | null;
    private onRequestConflictOverride = (conflict: Conflict) => { this.conflict = conflict; };
    private onResolveConflictOverride = () => { this.conflict = null; };

    @Prop({
        type: String,
        required: false,
    })
    public smallThumbUrl?: string;

    @Prop({
        type: Boolean,
        required: false,
        default: false,
    })
    public recommended!: boolean;

    public loaded = false;

    @Watch('filename')
    public updateFilenamePan() {
        this.$nextTick(() => {
            const viewport = this.$refs.filenameViewport as HTMLElement | undefined;
            const content = this.$refs.filenameContent as HTMLElement | undefined;
            const fadeWidth = this.isUploaded && !this.conflict && this.photoId ? 42 : 12;
            this.filenameOverflow = viewport && content ? Math.max(0, content.getBoundingClientRect().width - viewport.clientWidth + fadeWidth) : 0;
            this.filenameCycle = Math.max(10, this.filenameOverflow / 18 + 8);
        });
    }

    get saleOptionsActive() {
        return this.surcharge !== null && this.surcharge !== undefined || this.saleMode !== 'all';
    }

    public markSaleOptionsEdited() {
        if (this.saleDialogFromGroup) this.groupSaleOptionsEdited = true;
    }

    @Watch('session.pricelistId')
    onSessionPriceListChanged() {
        this.saleDialogOpen = false;
        this.priceList = null;
    }

    @Watch('surchargeEnabled')
    onSurchargeEnabled(value: boolean) { if (value) this.reductionEnabled = false; }
    @Watch('reductionEnabled')
    onReductionEnabled(value: boolean) { if (value) this.surchargeEnabled = false; }

    get adjustmentValid() {
        return (!this.surchargeEnabled || this.validSurcharge(this.draftSurcharge) === true) &&
            (!this.reductionEnabled || this.validSurcharge(this.draftReduction) === true && this.validReduction(this.draftReduction) === true);
    }

    get selectedAdjustment() {
        return this.surchargeEnabled ? Number(this.draftSurcharge.replace(',', '.')) :
            this.reductionEnabled ? -Number(this.draftReduction.replace(',', '.')) : 0;
    }

    get basePriceEnabled() {
        return !!this.priceList && !this.priceList.isSellingPackets && this.priceList.photoBasePrice.enabled;
    }

    get basePriceDisabledReason() {
        if (!this.priceList) return this.digitalSaleEnabled === null ? 'Trwa wczytywanie cennika sesji.' : 'Nie udało się wczytać cennika sesji.';
        return this.priceList.isSellingPackets ? 'W sprzedaży pakietowej nie ma bazowej ceny zdjęcia.' : 'Włącz bazową cenę zdjęcia w cenniku sesji.';
    }

    get previewBasePrice(): number|null {
        const price = this.priceList?.photoBasePrice;
        return price?.enabled && !this.priceList?.isSellingPackets ? Number(price.price) : null;
    }

    get previewPrice() {
        return Math.round(((this.previewBasePrice || 0) + this.selectedAdjustment) * 100) / 100;
    }

    get saleModesEnabled() {
        return this.digitalSaleEnabled === true && this.priceList?.printSaleDisabled === false && !!this.priceList.formats.length;
    }

    get digitalOnlyEnabled() {
        return this.saleModesEnabled && !this.priceList?.mustPickPrints;
    }

    get saleModesDisabledReason() {
        if (!this.priceList) return this.digitalSaleEnabled === null ? 'Trwa wczytywanie cennika sesji.' : 'Nie udało się wczytać cennika sesji.';
        if (!this.digitalSaleEnabled) return 'Włącz sprzedaż wersji cyfrowych w cenniku sesji.';
        return 'Włącz sprzedaż odbitek i dodaj przynajmniej jeden format w cenniku sesji.';
    }

    get digitalOnlyDisabledReason() {
        return this.saleModesEnabled ? 'Wyłącz obowiązek zakupu odbitek dla każdego zdjęcia w cenniku sesji.' : this.saleModesDisabledReason;
    }

    get noDigital() { return this.draftSaleMode === 'noDigital'; }
    set noDigital(value: boolean) { this.draftSaleMode = value ? 'noDigital' : 'all'; }
    get digitalOnly() { return this.draftSaleMode === 'digitalOnly'; }
    set digitalOnly(value: boolean) { this.draftSaleMode = value ? 'digitalOnly' : 'all'; }

    public validSurcharge(value: string) {
        return /^\d+(?:[.,]\d{1,2})?$/.test(String(value)) && Number(String(value).replace(',', '.')) <= 99999999 || 'Podaj kwotę od 0 do 99999999 zł (maks. 2 miejsca po przecinku)';
    }

    public validReduction(value: string) {
        return this.previewBasePrice !== null && Number(value.replace(',', '.')) <= this.previewBasePrice || 'Nowa cena nie może być niższa niż 0 zł';
    }

    public async openSaleDialog(photoIds?: string[]) {
        const priceListId = this.session.pricelistId;
        if (!priceListId) return;
        const requestId = ++this.saleDialogRequestId;
        this.saleDialogPhotoIds = photoIds || (this.photoId ? [this.photoId] : []);
        this.saleDialogFromGroup = !!photoIds;
        this.groupSaleOptionsEdited = false;
        const selected = this.saleDialogPhotoIds.map((id) => photosModule.remotePhotos[id]);
        const sameSurcharge = selected.length > 0 && selected.every((photo) => photo && photo.surcharge === selected[0]?.surcharge);
        const sameMode = selected.length > 0 && selected.every((photo) => photo && (photo.saleMode || 'all') === (selected[0]?.saleMode || 'all'));
        this.saleDialogMixed = !sameSurcharge || !sameMode;
        const initialSurcharge = sameSurcharge ? selected[0].surcharge : null;
        const initialMode = sameMode ? selected[0].saleMode || 'all' : 'all';
        this.surchargeEnabled = initialSurcharge !== null && initialSurcharge !== undefined && initialSurcharge >= 0;
        this.reductionEnabled = initialSurcharge !== null && initialSurcharge !== undefined && initialSurcharge < 0;
        this.draftSurcharge = String(Math.max(0, initialSurcharge || 0));
        this.draftReduction = String(Math.max(0, -(initialSurcharge || 0)));
        this.draftSaleMode = 'all';
        this.digitalSaleEnabled = null;
        this.priceList = null;
        this.savingSaleOptions = false;
        this.saleDialogOpen = true;
        try {
            const priceList = await api.getPriceList(priceListId);
            if (requestId !== this.saleDialogRequestId || !this.saleDialogOpen || this.session.pricelistId !== priceListId) return;
            this.priceList = priceList;
            this.digitalSaleEnabled = !!this.priceList.digitalPrice.enabled;
            if (!this.basePriceEnabled) {
                this.surchargeEnabled = false;
                this.reductionEnabled = false;
            }
            this.draftSaleMode = this.saleModesEnabled && (initialMode !== 'digitalOnly' || this.digitalOnlyEnabled) ? initialMode : 'all';
            await this.$nextTick();
            this.groupSaleOptionsEdited = false;
        } catch (_) {
            if (requestId !== this.saleDialogRequestId || !this.saleDialogOpen) return;
            this.digitalSaleEnabled = false;
            (this as any).$vToastify.error({ title: 'Nie udało się wczytać cennika sesji.' });
        }
    }

    public async saveSaleOptions() {
        if (!this.saleDialogPhotoIds.length || !this.priceList || !this.adjustmentValid || (this.saleDialogFromGroup && !this.groupSaleOptionsEdited)) return;
        const requestId = this.saleDialogRequestId;
        this.savingSaleOptions = true;
        try {
            const options = {
                surcharge: this.basePriceEnabled && (this.surchargeEnabled || this.reductionEnabled) ? this.selectedAdjustment : null,
                saleMode: this.saleModesEnabled && (this.draftSaleMode !== 'digitalOnly' || this.digitalOnlyEnabled) ? this.draftSaleMode : 'all' as 'all'|'noDigital'|'digitalOnly',
            };
            if (this.saleDialogFromGroup) {
                await photosModule.updateMultipleSaleOptions({ photoIds: this.saleDialogPhotoIds, ...options });
            } else {
                await photosModule.updateSaleOptions({ photoId: this.saleDialogPhotoIds[0], ...options });
            }
            if (requestId === this.saleDialogRequestId) this.saleDialogOpen = false;
        } catch (_) {
            if (requestId === this.saleDialogRequestId) (this as any).$vToastify.error({ title: 'Nie udało się zapisać ustawień zdjęcia.' });
        } finally {
            if (requestId === this.saleDialogRequestId) this.savingSaleOptions = false;
        }
    }

    @Watch('smallThumbUrl')
    private onThumbUrlChange() {
        this.loaded = false;
    }

    private onThumbLoad() {
        this.loaded = true;
        this.$emit('thumbSettled');
    }

    private onThumbError() {
        this.loaded = false;
        this.$emit('thumbSettled');
    }

    mounted() {
        window.addEventListener('resize', this.updateFilenamePan);
        const ifUploaded = (cb: () => void) => {
            if (this.isUploaded || this.progress >= 100) cb();
        }
        const ifNotMultiple = (cb: () => void) => (items: DataTransferItemList) => {
            if (items.length === 1) cb();
        };

        this.$on('globalDragstart', ifNotMultiple(() => ifUploaded(() => this.$el.classList.add('photoDrop'))));
        this.$on('globalDragend', ifNotMultiple(() => ifUploaded(() => this.$el.classList.remove('photoDrop'))));
        this.$on('dragenter', ifNotMultiple(() => ifUploaded(() => this.$el.classList.add('photoDropActive'))));
        this.$on('dragleave', ifNotMultiple(() => ifUploaded(() => this.$el.classList.remove('photoDropActive'))));

        this.$on('drop', (structure: FileStructure) => ifUploaded(() => this.handleDrop(structure)));

        replaceBus.$on('requestConflictOverride', this.onRequestConflictOverride);
        replaceBus.$on('resolveConflictOverride', this.onResolveConflictOverride);
        groupOperationBus.$on('openPhotoSaleOptions', this.onOpenGroupSaleOptions);
    }

    destroyed() {
        window.removeEventListener('resize', this.updateFilenamePan);
        replaceBus.$off('requestConflictOverride', this.onRequestConflictOverride);
        replaceBus.$off('resolveConflictOverride', this.onResolveConflictOverride);
        groupOperationBus.$off('openPhotoSaleOptions', this.onOpenGroupSaleOptions);
    }

    public async handleDrop(structure: FileStructure) {
        if (!this.photoId || structure.allFiles.length !== 1) return;
        if (this.ordered && !this.session.finalizationMode) {
            (this as any).$vToastify.error({
                title: 'Nie można podmienić.',
                body: 'Zdjęcie zostało kupione.',
            });
            return;
        }

        const finalize = this.ordered && !this.finalized && await this.finConfirmationModel.confirm('Sfinalizować to zdjęcie?');

        uploadModule.overwritePhoto({
            id: this.photoId,
            session: this.session,
            file: structure.allFiles[0],
        })

        if (finalize) {
            photosModule.finalize(this.photoId);
        }
    }

    public zoomIn() {
        this.$emit('zoomIn');
    }

    get photoStatus(): photoQueueStatus {
        if (uploadModule.statusByFile.has(this.photoFile!)) return uploadModule.statusByFile.get(this.photoFile!)!;
        return 'finished';
    }

    get progress() {
        const progress = uploadModule.photoPercent.get(this.photoFile!);
        return typeof progress === 'number' ? progress : 100;
    }

    get opacity() {
        const base = 0.2;
        if (this.photoStatus === 'waiting') {
            return base;
        } else if (this.isUploaded) {
            return 1;
        }

        return base + ((1 - base) * this.progress / 100);
    }

    get isUploaded() {
        return (!!this.photoId && this.photoStatus === 'finished') || this.progress === undefined;
    }

    public selectPhoto(event: MouseEvent) {
        if (this.isUploaded && !this.conflict) {
            this.$emit('selectionChanged', !this.checked, event);
        }
    }

    public selectCheckbox() {
        if (this.isUploaded && !this.conflict) {
            this.$emit('selectionChanged', !this.checked);
        }
    }

    public async deletePhoto() {
        if (this.isUploaded) {
            if (!await this.delConfirmationModel.confirm('Czy usunąć to zdjęcie?')) return;
            if (!await photosModule.deletePhoto(this.photoId!)) return;
        } else {
            uploadModule.cancelUpload({ file: this.photoFile! });
        }

        this.$emit('photoDeleted');
    }

    public blink() {
        const image = this.$refs.bg as HTMLElement | undefined;
        if (!image) return;
        image.classList.add('pulsate-bck');
        setTimeout(() => image.classList.remove('pulsate-bck'), 600);
    }

    public intersect() {
        this.intersected = true;
    }

    public async recommend() {
        if (!this.photoId) return;
        await this.runRecommendationWithCooldown(() => photosModule.recommend(this.photoId!));
    }

    public async unrecommend() {
        if (!this.photoId) return;
        await this.runRecommendationWithCooldown(() => photosModule.unrecommend(this.photoId!));
    }

    private async runRecommendationWithCooldown(action: () => Promise<void>) {
        const cooldownMs = 2000;
        const startedAt = Date.now();

        this.recommendationInProgress = true;
        try {
            await action();
        } finally {
            const elapsed = Date.now() - startedAt;
            const remaining = cooldownMs - elapsed;
            if (remaining > 0) {
                await new Promise((resolve) => setTimeout(resolve, remaining));
            }
            this.recommendationInProgress = false;
        }
    }

    public async toggleFinalization() {
        if (!this.photoId) return;
        
        if (this.finalized) {
            await photosModule.unfinalize(this.photoId);
        } else {
            await photosModule.finalize(this.photoId);
        }
    }
}
